import { FunctionComponent, createContext, ReactNode, ReactElement, useEffect, useRef } from 'react';
import { AuthUser } from '@/core/types';
import { useAuthenticatedUser, useLogout, useStopSpoof } from '@/queries/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { removeAuthData } from '@/shared/utils/auth';
import Backdrop from '@/components/ui/backdrop';
import useFilters from '@/shared/context/filter-context';

export interface IAuthContext {
	user?: AuthUser;
	logout: () => void;
	isSpoofing: boolean;
	stopSpoofing: () => void;
	isStoppingSpoofing: boolean;
}

export const AuthContext = createContext<IAuthContext>({
	logout: () => {},
	isSpoofing: false,
	stopSpoofing: () => {},
	isStoppingSpoofing: false,
});

export const AuthProvider: FunctionComponent<{ children: ReactNode }> = ({
	children,
}): ReactElement => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const { queryKey, query } = useAuthenticatedUser();
	const { data: user } = query;

	const { mutate: logout, isLoading: isLoggingOut } = useLogout({
		onSuccess: () => {
			queryClient.removeQueries({ queryKey });
			removeAuthData();
			navigate('/auth/login', { replace: true });
		},
	});

	const { mutate: stopSpoofing, isLoading: isStoppingSpoofing } = useStopSpoof();

	const isSpoofing = !!user?.spoofedById;

	// Sticky filters must not leak between users (logout, spoof start/stop).
	const { resetFilters } = useFilters();
	const lastUserId = useRef(user?.id);
	useEffect(() => {
		if (lastUserId.current !== user?.id) {
			if (lastUserId.current) resetFilters();
			lastUserId.current = user?.id;
		}
	}, [user?.id, resetFilters]);

	return (
		<AuthContext.Provider value={{ user, logout, isSpoofing, stopSpoofing, isStoppingSpoofing }}>
			{(isLoggingOut || isStoppingSpoofing) && <Backdrop />}
			{children}
		</AuthContext.Provider>
	);
};
